import { inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { Api, Subscription } from 'bimplus-websdk';

import { BIMPLUS_API } from '@web-portal/domains/shared/bimplus-core';
import { CreateTeamResponse, GetTeamsParams } from './teams.interface';
import { TeamMapper } from '@web-portal/domains/team/anti-corruption-layer';

/**
 * TeamsService - Adapter over bimplus-websdk Teams API.
 */
@Injectable({
  providedIn: 'root',
})
export class TeamsAdapterService {
  private readonly api: Api = inject(BIMPLUS_API);

  /**
   * Creates a new team.
   *
   * @param teamData - The data for the new team, typically a JSON string with team details.
   * @returns Observable of the created team (type depends on SDK response).
   */
  createTeam(teamData: { name: string }): Observable<CreateTeamResponse> {
    return this.api.rxRequest(
      this.api.teams.postTeam(JSON.stringify(teamData)) as Promise<CreateTeamResponse>
    );
  }

  /**
   * Retrieves all teams for the current user.
   *
   * Returns a list of teams that the current user has access to.
   * If filterEmpty is true, teams without projects are excluded.
   *
   * @param params - Optional parameters for the request.
   * @returns Observable of Team array.
   */
  getTeams(params?: GetTeamsParams) {
    return this.api
      .rxRequest(this.api.teams.get(undefined, params?.filterEmpty))
      .pipe(
        map(rawTeams =>
          rawTeams
            .filter(team => team.slug !== 'bimplus-demo')
            .map(team => TeamMapper.mapToTeam(team))
        )
      );
  }

  /**
   * Retrieves a specific team by ID.
   *
   * @param teamId - The ID of the team to retrieve.
   * @returns Observable of Team array (SDK returns array even for single team).
   */
  getTeamById(teamId: string) {
    return this.api
      .rxRequest(this.api.teams.get(teamId))
      .pipe(map(rawTeam => TeamMapper.mapToTeam(rawTeam)));
  }

  /**
   * Retrieves account settings/subscriptions for a team.
   *
   * @param teamId - Optional team ID. If not provided, uses the current team.
   * @returns Observable of Subscription array.
   */
  getAccountSettings(teamId?: string): Observable<Subscription[]> {
    return this.api.rxRequest(this.api.teams.getAccountSettings(teamId));
  }
}
